import { useEffect, useMemo, useState } from "react";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { formatUnits } from "viem";
import { useT } from "../lib/i18n";
import type { RedKeel } from "../config/networks";
import type { EstadoVault } from "../hooks/useVault";
import type { Acc } from "../hooks/useAcciones";
import { ANCHO_MIN, planAncho, planAporte, twapDe, type Entrada, type Plan as PlanAchicar } from "../lib/achicar";

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  { type: "function", name: "observe", stateMutability: "view", inputs: [{ type: "uint32[]" }],
    outputs: [{ type: "int56[]" }, { type: "uint160[]" }] },
] as const;

const VAULT_FEE_ABI = [
  { type: "function", name: "fee", stateMutability: "view", inputs: [], outputs: [{ type: "uint24" }] },
] as const;

const ERC20_ABI = [{
  type: "function", name: "balanceOf", stateMutability: "view",
  inputs: [{ name: "a", type: "address" }], outputs: [{ type: "uint256" }],
}] as const;

/** Anchos de los escenarios (amplitud (b−a)/a), los mismos que la herramienta de referencia. */
const PRESETS = [0.25, 0.20, 0.15, 0.10, 0.08, 0.06, 0.05];

const anchoPctATicks = (totalPct: number) => Math.max(2, Math.round(Math.log(1 + totalPct / 100) / Math.log(1.0001)));
const ticksAAnchoPct = (ticks: number) => (Math.pow(1.0001, ticks) - 1) * 100;
const fmt = (b: bigint, d: number, dec = 2) =>
  Number(formatUnits(b, d)).toLocaleString(undefined, { maximumFractionDigits: dec });

/**
 * EL PLAN DEL RE-CENTRADO SIMÉTRICO, CON SU APORTE (piscinas que NO están en pérdida; las que
 * están bajo el agua van por la reinyección promediada de `lib/achicar.ts`).
 *
 * `cuantoParaAchicar` solo sabe de la BANDA DE RECUPERACIÓN: la que devuelve el capital sin vender.
 * Cuando la pérdida no cabe en el ancho pedido —con un 7% abajo no hay banda de ±2% que recupere
 * nada, aportes lo que aportes— el quoter contesta `ok = false`, y hasta ahora la app ofrecía en su
 * lugar un re-centrado simétrico "sin aportar" SIN comprobar que hubiera con qué llenarlo.
 *
 * Medido en un vault real (USDG/RBLX, 2026-09-17): con el precio pegado al borde de abajo, la
 * posición estaba 96,9% en RBLX, y ese "achicar sin aportar" habría desplegado 67 de 2.194 USDG de
 * valor — el 93,5% del capital se habría quedado suelto en el vault, sin cobrar una comisión, hasta
 * el siguiente barrido. Exactamente lo contrario de lo que se pulsa al achicar.
 *
 * Así que la cuenta se hace aquí: cuánto estable hay que aportar para que el activo ENTERO quepa en
 * el rango nuevo, y —si no se aporta— cuánto se quedaría fuera. Es la misma aritmética de Uniswap
 * V3 que usa el contrato (`_mintRange` despliega la parte que casa y deja libre el resto), con las
 * raíces en crudo para no arrastrar los decimales.
 */
type Plan = {
  below: number; above: number; anchoTicks: number;
  /** estable a aportar para desplegar TODO el activo, en unidades crudas */
  aporte: bigint;
  /** activo que quedaría FUERA de la posición si no se aporta nada (humano) */
  fueraActivo: number;
  /** ese sobrante, como % del valor total */
  fueraPct: number;
};

function planSimetrico(v: EstadoVault, objetivoPctTotal: number): Plan {
  const sp = Math.max(1, v.spacing);
  const half = Math.max(sp, Math.round(anchoPctATicks(objetivoPctTotal) / 2 / sp) * sp);
  const suelo = (t: number) => Math.floor(t / sp) * sp;
  const lo = suelo(v.tick - half);
  const hi = suelo(v.tick + half);
  const raiz = (t: number) => Math.pow(1.0001, t / 2);
  const s = raiz(v.tick), sa = raiz(lo), sb = raiz(hi);

  // Lo que habrá DISPONIBLE cuando la posición se cierre: lo que lleva dentro + lo suelto, menos lo
  // que el vault retiene y nunca despliega (bolsa de gas, ganancia apartada y reserva de DCA).
  const retenido0 = Number(v.gasBudget0) + Number(v.profit0) + (v.refEsToken0 ? Number(v.reserva) : 0);
  const retenido1 = Number(v.gasBudget1) + Number(v.profit1) + (!v.refEsToken0 ? Number(v.reserva) : 0);
  const have0 = Math.max(0, (v.enPosicion.a0 + v.libre.a0) * 10 ** v.token0.decimals - retenido0);
  const have1 = Math.max(0, (v.enPosicion.a1 + v.libre.a1) * 10 ** v.token1.decimals - retenido1);

  const porToken0 = 1 / s - 1 / sb; // token0 que pide una unidad de liquidez
  const porToken1 = s - sa;         // token1 que pide una unidad de liquidez

  let aporte = 0, fueraRaw = 0, decimalesActivo: number, valorTotal: number;
  if (v.refEsToken0) {
    // estable = token0, activo = token1 → la liquidez la manda el activo y falta estable
    const lActivo = porToken1 > 0 ? have1 / porToken1 : 0;
    const necesita0 = lActivo * porToken0;
    aporte = Math.max(0, necesita0 - have0);
    const lCabe = Math.min(porToken0 > 0 ? have0 / porToken0 : 0, lActivo);
    fueraRaw = Math.max(0, have1 - lCabe * porToken1);
    decimalesActivo = v.token1.decimals;
    valorTotal = have0 + have1 / (s * s); // token1 valorado en token0
  } else {
    // espejo: estable = token1, activo = token0
    const lActivo = porToken0 > 0 ? have0 / porToken0 : 0;
    const necesita1 = lActivo * porToken1;
    aporte = Math.max(0, necesita1 - have1);
    const lCabe = Math.min(porToken1 > 0 ? have1 / porToken1 : 0, lActivo);
    fueraRaw = Math.max(0, have0 - lCabe * porToken0);
    decimalesActivo = v.token0.decimals;
    valorTotal = have1 + have0 * s * s;
  }
  const fueraValor = v.refEsToken0 ? fueraRaw / (s * s) : fueraRaw * s * s;

  return {
    below: half, above: half, anchoTicks: half * 2,
    aporte: BigInt(Math.max(0, Math.floor(aporte))),
    fueraActivo: fueraRaw / 10 ** decimalesActivo,
    fueraPct: valorTotal > 0 ? (fueraValor / valorTotal) * 100 : 0,
  };
}

export const CSS_ACHICAR = `
@keyframes ach-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes ach-pop { from { opacity: 0; transform: translateY(10px) scale(.97) } to { opacity: 1; transform: none } }
@keyframes ach-spin { to { transform: rotate(360deg) } }
@keyframes ach-pulse { 0%,100% { opacity: .5 } 50% { opacity: .9 } }
.ach-ov { position: fixed; inset: 0; z-index: 70; display: grid; place-items: center; padding: 16px;
  background: rgba(8,8,11,.62); backdrop-filter: blur(3px); animation: ach-in .16s ease-out; }
.ach-box { width: 100%; max-width: 480px; color: #ececf1; border-radius: 18px;
  background: linear-gradient(180deg,#191920,#141419); border: 1px solid #2b2b34;
  box-shadow: 0 1px 0 rgba(255,255,255,.05) inset, 0 10px 24px -8px rgba(0,0,0,.7), 0 40px 70px -20px rgba(0,0,0,.6);
  animation: ach-pop .22s cubic-bezier(.32,.72,0,1); overflow-x: hidden; overflow-y: auto; max-height: calc(100dvh - 32px); }
.ach-box.wide { max-width: 900px; }
/* Ancho: a la izquierda lo que eliges, a la derecha el resultado y el botón. En móvil, una columna. */
.ach-cols { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap: 0 26px; }
.ach-cols > .der { display: flex; flex-direction: column; padding-top: 10px; }
.ach-cols > .der > .ach-card:first-child { margin-top: 0; }
.ach-cols .ach-foot { margin-top: auto; padding-top: 14px; }
@media (max-width: 760px) { .ach-cols { grid-template-columns: 1fr; } .ach-cols > .der { padding-top: 0; } }
.ach-gat { display: grid; grid-template-columns: repeat(auto-fill, minmax(112px,1fr)); gap: 7px; margin-top: 8px; }
.ach-gat .ach-chip { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; padding: 8px 10px; min-width: 0; text-align: left; }
.ach-gat .ach-chip small { font: 500 11.5px ui-monospace,monospace; color: #9a9aa6; white-space: nowrap; }
.ach-gat .ach-chip.ya small { color: #4bd6a0; }
.ach-pad { padding: 18px 20px 20px; }
.ach-x { border: 1px solid #2b2b34; background: #1e1e25; color: #9a9aa6; width: 30px; height: 30px; border-radius: 9px;
  cursor: pointer; font-size: 15px; line-height: 1; transition: transform .12s ease, background .12s ease, color .12s; }
.ach-x:hover { background: #26262e; color: #ececf1; } .ach-x:active { transform: scale(.92); }
.ach-title { font-size: 18px; font-weight: 650; letter-spacing: -.01em; display: flex; align-items: center; gap: 9px; }
.ach-ico { width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center; font-size: 15px;
  background: radial-gradient(120% 120% at 30% 20%, #ff7a4d, #d64f2a); color: #fff; box-shadow: 0 2px 8px -2px rgba(214,79,42,.6); }
.ach-sub { color: #8f8f9c; font-size: 13px; line-height: 1.55; margin: 8px 0 0; }
.ach-diag { margin: 16px 0 6px; }
.ach-track { position: relative; height: 46px; border-radius: 11px; background: #0d0d11; border: 1px solid #24242c; overflow: hidden; }
.ach-band { position: absolute; top: 8px; bottom: 8px; border-radius: 6px; transition: left .24s cubic-bezier(.32,.72,0,1), width .24s cubic-bezier(.32,.72,0,1); }
.ach-cur { background: repeating-linear-gradient(45deg,#2a2a34,#2a2a34 6px,#252530 6px,#252530 12px); }
.ach-new { background: linear-gradient(180deg, rgba(255,122,77,.9), rgba(214,79,42,.85)); box-shadow: 0 0 0 1px rgba(255,150,110,.5) inset, 0 4px 14px -4px rgba(214,79,42,.7); }
.ach-price { position: absolute; top: 3px; bottom: 3px; width: 2px; left: 50%; transform: translateX(-1px); background: #efeff4; box-shadow: 0 0 10px rgba(255,255,255,.5); }
.ach-pricelbl { position: absolute; top: -18px; left: 50%; transform: translateX(-50%); font-size: 10.5px; color: #b9b9c6; white-space: nowrap; letter-spacing: .02em; }
.ach-diaglbl { display: flex; justify-content: space-between; font-size: 11px; color: #6f6f7c; margin-top: 5px; font-variant-numeric: tabular-nums; }
.ach-lbl { font-size: 12px; color: #8f8f9c; letter-spacing: .03em; text-transform: uppercase; margin: 16px 0 8px; display: flex; justify-content: space-between; align-items: baseline; }
.ach-chips { display: flex; gap: 7px; flex-wrap: wrap; margin-bottom: 12px; }
.ach-chip { flex: 1; min-width: 56px; border: 1px solid #2b2b34; background: #16161c; color: #cfcfd8; border-radius: 10px; padding: 8px 6px;
  font: 600 13.5px ui-monospace,monospace; font-variant-numeric: tabular-nums; cursor: pointer;
  transition: transform .12s ease, border-color .12s, background .12s, color .12s; }
.ach-chip:hover { border-color: #3a3a46; } .ach-chip:active { transform: scale(.96); }
.ach-chip.on { background: rgba(214,79,42,.16); border-color: #d64f2a; color: #ffb79c; }
.ach-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 6px; outline: none; cursor: pointer;
  background: #26262e; }
.ach-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #ff7a4d; cursor: pointer;
  box-shadow: 0 0 0 4px rgba(214,79,42,.2), 0 2px 6px rgba(0,0,0,.5); transition: transform .12s ease; }
.ach-slider::-webkit-slider-thumb:active { transform: scale(1.15); }
.ach-slider::-moz-range-thumb { width: 18px; height: 18px; border: 0; border-radius: 50%; background: #ff7a4d; cursor: pointer; }
.ach-card { position: relative; margin: 14px 0 4px; border-radius: 13px; padding: 15px 16px;
  background: linear-gradient(180deg,#101014,#0d0d11); border: 1px solid #24242c; }
.ach-card.load { animation: ach-pulse 1.1s ease-in-out infinite; }
.ach-big { font-size: 25px; font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; display: flex; align-items: baseline; gap: 7px; }
.ach-unit { font-size: 15px; font-weight: 600; color: #9a9aa6; }
.ach-tag { margin-left: auto; font-size: 12px; font-weight: 600; color: #ffb79c; background: rgba(214,79,42,.15); padding: 3px 9px; border-radius: 20px; font-variant-numeric: tabular-nums; }
.ach-row { display: flex; justify-content: space-between; align-items: center; font-size: 13px; padding: 6px 0; border-top: 1px solid #1e1e26; }
.ach-row:first-of-type { border-top: 0; margin-top: 10px; }
.ach-k { color: #8f8f9c; } .ach-v { font-variant-numeric: tabular-nums; color: #d8d8e0; }
.ach-ok { color: #4bd6a0; }
.ach-err { color: #ff7a7a; font-size: 13px; background: rgba(255,90,90,.08); border: 1px solid rgba(255,90,90,.25); border-radius: 10px; padding: 10px 12px; margin: 10px 0 2px; }
.ach-foot { display: flex; gap: 9px; justify-content: flex-end; margin-top: 16px; }
.ach-btn { border: 1px solid #2b2b34; background: #1c1c23; color: #d8d8e0; border-radius: 11px; padding: 11px 15px; font-size: 14.5px; font-weight: 600;
  cursor: pointer; transition: transform .12s ease, background .12s, opacity .12s; }
.ach-btn:hover { background: #23232b; } .ach-btn:active { transform: translateY(1px) scale(.99); }
.ach-btn.pri { flex: 1; color: #fff; border-color: transparent; font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg,#ff7a4d,#e0562e); box-shadow: 0 4px 14px -4px rgba(214,79,42,.6), 0 1px 0 rgba(255,255,255,.2) inset; }
.ach-btn.pri:disabled, .ach-btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }
.ach-spin { width: 15px; height: 15px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: ach-spin .7s linear infinite; vertical-align: -2px; }
@media (prefers-reduced-motion: reduce) { .ach-ov,.ach-box,.ach-band,.ach-card.load { animation: none !important; transition: none !important; } }
`;

export function ModalAchicar({ v, red, acciones, anchoActualPct, cerrar }: {
  v: EstadoVault; red: RedKeel; acciones: Acc; anchoActualPct: number; cerrar: () => void;
}) {
  const t = useT();
  const config = useConfig();
  const estable = v.refEsToken0 ? v.token0 : v.token1;
  const activo = v.refEsToken0 ? v.token1 : v.token0;
  const dRef = estable.decimals;

  // Lo que se lee de la cadena al abrir: precio crudo, TWAP (el centro que usa el contrato),
  // comisión de la pool y saldo del dueño.
  const [cadena, setCadena] = useState<{ sqrtP: number; tick: number; twap: number; fee: number } | null>(null);
  const [saldoDueno, setSaldoDueno] = useState<bigint | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const publico = getPublicClient(config, { chainId: red.idCadena });
        if (!publico) throw new Error("sin RPC");
        const [slot0, obs, fee] = await publico.multicall({
          allowFailure: false,
          contracts: [
            { address: v.pool, abi: POOL_ABI, functionName: "slot0" },
            { address: v.pool, abi: POOL_ABI, functionName: "observe", args: [[v.twapWindow, 0]] },
            { address: v.direccion, abi: VAULT_FEE_ABI, functionName: "fee" },
          ],
        });
        if (!vivo) return;
        setCadena({
          sqrtP: Number(slot0[0]) / 2 ** 96,
          tick: Number(slot0[1]),
          twap: twapDe(BigInt(obs[0][0] ?? 0n), BigInt(obs[0][1] ?? 0n), v.twapWindow),
          fee: Number(fee) / 1e6,
        });
      } catch (e) {
        if (vivo) setError(e instanceof Error ? e.message.split("\n")[0]! : String(e));
      }
    })();
    (async () => {
      try {
        const publico = getPublicClient(config, { chainId: red.idCadena });
        if (!publico) return;
        const b = (await publico.readContract({
          address: estable.address, abi: ERC20_ABI, functionName: "balanceOf", args: [v.dueno],
        })) as bigint;
        if (vivo) setSaldoDueno(b);
      } catch { /* el aviso de saldo es una ayuda, no una guardia */ }
    })();
    return () => { vivo = false; };
  }, [v.pool, v.direccion, v.twapWindow, red.idCadena, estable.address, v.dueno]); // eslint-disable-line

  // Lo desplegable HOY (sin reserva, bolsa de gas ni ganancia apartada) y el objetivo sin la
  // reserva: la reserva se queda en estable fuera de la posición y vuelve tal cual al salir.
  const entrada: Entrada | null = useMemo(() => {
    if (!cadena) return null;
    const precioCrudo = cadena.sqrtP * cadena.sqrtP; // token1 crudo por token0 crudo
    const ret0 = Number(v.gasBudget0) + Number(v.profit0) + (v.refEsToken0 ? Number(v.reserva) : 0);
    const ret1 = Number(v.gasBudget1) + Number(v.profit1) + (!v.refEsToken0 ? Number(v.reserva) : 0);
    const have0 = Math.max(0, (v.enPosicion.a0 + v.libre.a0) * 10 ** v.token0.decimals - ret0);
    const have1 = Math.max(0, (v.enPosicion.a1 + v.libre.a1) * 10 ** v.token1.decimals - ret1);
    const est = (v.refEsToken0 ? have0 : have1) / 10 ** dRef;
    const act = v.refEsToken0 ? have1 / precioCrudo : have0 * precioCrudo; // en crudo del estable
    return {
      refEsToken0: v.refEsToken0, sqrtP: cadena.sqrtP, tick: cadena.tick, twap: cadena.twap,
      spacing: Math.max(1, v.spacing), fee: cadena.fee,
      D: est + act / 10 ** dRef, estable: est,
      T: Number(v.costBasisRef - v.reserva) / 10 ** dRef,
    };
  }, [cadena, v]); // eslint-disable-line

  const bajoElAgua = !!entrada && entrada.D < entrada.T;
  const logicaOk = (red.logicasAchicar ?? []).some((l) => l.toLowerCase() === v.rebalancer.toLowerCase());

  // Amplitud ACTUAL (b−a)/a y escenarios más estrechos, como la herramienta de referencia.
  const anchoHoy = v.rango ? Math.pow(1.0001, v.rango.hi - v.rango.lo) - 1 : anchoActualPct / 100;
  const [s, setS] = useState(0.03);
  const [elegido, setElegido] = useState<number | null>(null);
  const [aporteTxt, setAporteTxt] = useState("");

  const escenarios = useMemo(() => {
    if (!entrada || !bajoElAgua) return [];
    return PRESETS.filter((w) => w < anchoHoy - 1e-9)
      .map((w) => ({ w, plan: planAncho(entrada, w, s) }))
      .filter((x): x is { w: number; plan: PlanAchicar } => !!x.plan);
  }, [entrada, bajoElAgua, anchoHoy, s]);

  const aporteDisp = Number(aporteTxt.replace(",", "."));
  const planPorAporte = useMemo(() => {
    if (!entrada || !bajoElAgua || !(aporteDisp > 0)) return null;
    return planAporte(entrada, aporteDisp, s, Math.max(anchoHoy, ANCHO_MIN));
  }, [entrada, bajoElAgua, aporteDisp, s, anchoHoy]);

  // El plan que se firma: el del aporte escrito si lo hay, si no el escenario elegido (20% o el
  // más estrecho disponible por defecto).
  const porDefecto = escenarios.find((x) => Math.abs(x.w - 0.2) < 1e-9) ?? escenarios[escenarios.length - 1];
  const delEscenario = escenarios.find((x) => x.w === elegido) ?? porDefecto;
  const plan: PlanAchicar | null = aporteTxt ? planPorAporte : delEscenario?.plan ?? null;

  // Piscinas que NO están en pérdida: se mantiene el re-centrado simétrico de siempre.
  const actual = anchoActualPct > 0.2 ? anchoActualPct : 4;
  const [objetivoSim, setObjetivoSim] = useState(() => Math.round((actual / 2) * 10) / 10);
  const sim = useMemo(() => (!bajoElAgua ? planSimetrico(v, objetivoSim) : null), [bajoElAgua, v, objetivoSim]);

  const aporteWei = (x: number) => BigInt(Math.ceil(x * 10 ** dRef));
  const importe = bajoElAgua ? (plan ? aporteWei(plan.aporte) : 0n) : sim ? sim.aporte : 0n;
  const faltaSaldo = saldoDueno !== null && importe > saldoDueno;

  // La forma que tenía el rango ANTES: se devuelve en una segunda firma, para que cuando la
  // piscina salga por el borde con su capital el robot re-centre con tu rango de siempre y no con
  // esta banda torcida (que compraría el activo otra vez).
  const restaurar = v.ticksBelow > 0 && v.ticksAbove > 0
    ? { below: v.ticksBelow, above: v.ticksAbove }
    : { below: v.halfWidthTicks, above: v.halfWidthTicks };

  async function firmar() {
    if (bajoElAgua) {
      if (!plan) return;
      await acciones.achicarRango(plan.below, plan.above, aporteWei(plan.aporte), restaurar);
    } else {
      if (!sim) return;
      await acciones.achicarRango(sim.below, sim.above, sim.aporte);
    }
    cerrar();
  }

  const fmtN = (n: number, dec = 2) => n.toLocaleString(undefined, { maximumFractionDigits: dec });
  // Precio en estables por unidad de activo, desde un tick de la pool.
  const pTick = (tk: number) => {
    const p = Math.pow(1.0001, tk) * 10 ** (v.token0.decimals - v.token1.decimals);
    return v.refEsToken0 ? 1 / p : p;
  };
  const fmtP = (p: number) => p.toLocaleString(undefined, { maximumSignificantDigits: 5 });

  const ocupado = !!acciones.haciendo;
  const cargando = !entrada && !error;

  // Con el modal ancho el aviso de saldo y los botones van en la columna derecha, bajo el resultado.
  const ancho = bajoElAgua && logicaOk && !error && !cargando;
  const aviso = faltaSaldo && (
            <div className="ach-err">
              {t("Te faltan {n} {s} en la wallet para este aporte.", { n: fmt(importe - (saldoDueno ?? 0n), dRef, 2), s: estable.symbol })}
            </div>
  );
  const pie = !(bajoElAgua && !logicaOk) && !error && !cargando && (
            <div className="ach-foot">
              <button className="ach-btn" onClick={cerrar}>{t("Cancelar")}</button>
              <button className="ach-btn pri" onClick={firmar}
                disabled={ocupado || faltaSaldo || (bajoElAgua ? !plan : !sim)}>
                {acciones.haciendo === "achicarRango"
                  ? <><span className="ach-spin" /> {t("Achicando…")}</>
                  : importe > 0n ? t("Aportar {n} {s} y achicar", { n: fmt(importe, dRef), s: estable.symbol })
                  : t("Achicar")}
              </button>
            </div>
  );

  return (
    <div className="ach-ov" onClick={cerrar}>
      <style>{CSS_ACHICAR}</style>
      <div className={"ach-box" + (ancho ? " wide" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="ach-pad">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="ach-title"><span className="ach-ico">⤢</span>{t("Achicar el rango")}</div>
            <button className="ach-x" onClick={cerrar} aria-label="cerrar">✕</button>
          </div>

          {error ? <div className="ach-err">{error}</div>
            : cargando ? <div className="ach-card load"><div className="ach-big"><span style={{ color: "#6f6f7c" }}>·····</span></div></div>
            : bajoElAgua ? (
              !logicaOk ? (
                <>
                  <p className="ach-sub">{t("Tu piscina está por debajo de tu capital. Para achicar con reinyección promediada (el aporte compra {a} y acerca el techo) tu piscina necesita la lógica nueva.", { a: activo.symbol })}</p>
                  {v.hayActualizacion
                    ? <div className="ach-foot"><button className="ach-btn" onClick={cerrar}>{t("Cancelar")}</button>
                        <button className="ach-btn pri" disabled={ocupado} onClick={() => acciones.actualizarLogica()}>{t("Actualizar la lógica")}</button></div>
                    : <div className="ach-err">{t("Tu piscina es de una versión anterior de Keel y no puede recibir esta lógica. Para usarlo: retira tu piscina y créala de nuevo con «Rescatar una posición» indicando tu capital como objetivo; luego podrás achicar el rango.")}</div>}
                </>
              ) : (
                <div className="ach-cols"><div>
                  <p className="ach-sub">
                    {t("Reinyección promediada: se cierra tu piscina y se reabre con lo que vale hoy más tu aporte, en un rango más estrecho. Tu aporte COMPRA {a} y, cuando el precio toque el techo, sales con tu capital más lo que aportaste.", { a: activo.symbol })}
                  </p>
                  <div className="ach-row" style={{ marginTop: 10 }}><span className="ach-k">{t("Vale hoy / tu capital")}</span>
                    <span className="ach-v">{fmtN(entrada!.D)} / {fmtN(entrada!.T)} {estable.symbol}</span></div>

                  <div className="ach-lbl"><span>{t("% en {s} al abrir", { s: estable.symbol })}</span></div>
                  <div className="ach-chips">
                    {[0.02, 0.03, 0.05].map((x) => (
                      <button key={x} className={"ach-chip" + (s === x ? " on" : "")} onClick={() => setS(x)}>{Math.round(x * 100)}%</button>
                    ))}
                  </div>

                  <div className="ach-lbl"><span>{t("Ancho del rango nuevo")}</span></div>
                  <div className="ach-chips">
                    {escenarios.map((x) => (
                      <button key={x.w} className={"ach-chip" + (!aporteTxt && delEscenario?.w === x.w ? " on" : "")}
                        onClick={() => { setAporteTxt(""); setElegido(x.w); }}>{Math.round(x.w * 100)}%</button>
                    ))}
                  </div>
                  <div className="ach-lbl"><span>{t("O escribe cuánto puedes aportar")}</span></div>
                  <input className="ach-chip" style={{ width: "100%", textAlign: "left", padding: "10px 12px" }} inputMode="decimal"
                    placeholder={estable.symbol} value={aporteTxt} onChange={(e) => setAporteTxt(e.target.value)} />
                </div><div className="der">
                  {!plan ? (
                    <div className="ach-err">{aporteTxt
                      ? t("Con ese aporte no hay un rango razonable que te lleve a tu capital. Prueba con más.")
                      : t("No hay un rango más estrecho posible con el precio de ahora.")}</div>
                  ) : (
                    <div className="ach-card">
                      <div className="ach-big">
                        {plan.aporte > 0 ? <>{fmtN(plan.aporte)}<span className="ach-unit">{estable.symbol}</span></> : <span style={{ fontSize: 20 }}>{t("No hace falta aportar")}</span>}
                        <span className="ach-tag">{t("aportas")}</span>
                      </div>
                      {/* Lo que se cobra en el borde lo GARANTIZA el contrato: tras comprar, arma la
                          banda con lo que de verdad hay contra capital + aporte. El rango y la subida
                          son la estimación de la app: si la compra mueve el precio, el borde queda
                          algo más lejos (medido en fork: ~0,1-0,2%). */}
                      <div className="ach-row"><span className="ach-k">{t("Sales con (en el techo)")}</span>
                        <span className="ach-v ach-ok">{fmtN(entrada!.T + plan.aporte)} {estable.symbol}</span></div>
                      <div className="ach-row-exp" style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: -2 }}>{t("Tu capital + tu aporte. Lo asegura el contrato.")}</div>
                      <div className="ach-row"><span className="ach-k">{t("Rango nuevo (aprox.)")}</span>
                        <span className="ach-v">{fmtP(Math.min(pTick(plan.lo), pTick(plan.hi)))} – {fmtP(Math.max(pTick(plan.lo), pTick(plan.hi)))} ({fmtN(plan.ancho * 100, 1)}%)</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Subida necesaria de {a}", { a: activo.symbol })}</span>
                        <span className="ach-v">≈ +{fmtN(plan.subida, 1)}%</span></div>
                      <div className="ach-row"><span className="ach-k">{t("Abres con")}</span>
                        <span className="ach-v">{fmtN((1 - plan.fracEstable) * 100, 1)}% {activo.symbol} · {fmtN(plan.fracEstable * 100, 1)}% {estable.symbol}</span></div>
                    </div>
                  )}
                  {plan && (
                    <div className="ach-err" style={{ color: "#f2d28e", background: "rgba(232,183,106,.08)", borderColor: "rgba(232,183,106,.3)" }}>
                      {t("Apuesta a la recuperación: si {a} cae más de un {c}%, sales del rango por abajo con todo en {a} (incluido lo que aportaste). No incluye comisiones ganadas ni el gas.", { a: activo.symbol, c: fmtN(plan.colchon, 1) })}
                    </div>
                  )}
                  <p className="ach-sub" style={{ fontSize: 12 }}>{t("Son dos firmas (más la aprobación): achicar y dejar tu forma de rango de siempre para cuando recuperes.")}</p>
                  {aviso}
                  {pie}
                </div></div>
              )
            ) : (
              <>
                <p className="ach-sub">
                  {t("Concentra tu liquidez para cobrar más comisiones. No se vende nada de tu {a}: el rango estrecho se llena con lo que hay, y por eso a veces hay que APORTAR {s} para que entre todo.", { a: activo.symbol, s: estable.symbol })}
                </p>
                <div className="ach-lbl"><span>{t("Ancho objetivo")}</span><span style={{ color: "#cfcfd8", fontVariantNumeric: "tabular-nums" }}>±{(objetivoSim / 2).toFixed(2)}%</span></div>
                <input className="ach-slider" type="range" min={0.2} max={Math.max(0.4, Math.round(actual * 10) / 10)} step={0.1} value={objetivoSim}
                  onChange={(e) => setObjetivoSim(Number(e.target.value))} />
                {sim && (
                  <div className="ach-card">
                    <div className="ach-big">
                      {sim.aporte > 0n ? <>{fmt(sim.aporte, dRef)}<span className="ach-unit">{estable.symbol}</span></> : <span style={{ fontSize: 20 }}>{t("No hace falta aportar")}</span>}
                      <span className="ach-tag">{sim.aporte > 0n ? t("aportas") : t("re-centrar")}</span>
                    </div>
                    <div className="ach-row"><span className="ach-k">{t("Rango resultante")}</span><span className="ach-v">±{(ticksAAnchoPct(sim.anchoTicks) / 2).toFixed(2)}%</span></div>
                    <div className="ach-row"><span className="ach-k">{t("Entra en la posición")}</span><span className="ach-v ach-ok">{t("todo tu capital ✓")}</span></div>
                  </div>
                )}
              </>
            )}

          {!ancho && aviso}
          {!ancho && pie}
        </div>
      </div>
    </div>
  );
}
